import { Link } from "react-router-dom";
import { useContent } from "../../context/ContentContext";
import { Reveal } from "./Reveal";
import { ArrowUpRight, Play, Images } from "lucide-react";
import { galleryCover, mediaItem } from "../../lib/media";
import { FramedImage } from "./FramedImage";
import { isVisible, isLocked, isPast, pastBadgeLabel, temporalBadge } from "../../lib/publish";

export const publishedNews = (news) =>
  (news?.items || [])
    .filter((it) => isVisible(it))
    .map((it, i) => ({ ...it, _i: i }))
    .sort((a, b) => (b.pinned === true) - (a.pinned === true) || (a.order ?? a._i) - (b.order ?? b._i));

const Thumb = ({ it }) => {
  const src = it.mediaType === "slideshow" ? galleryCover(it.gallery) : it.image;
  const firstMedia = it.mediaType === "slideshow" ? mediaItem((it.gallery || [])[0]) : null;
  const isVideoCover = it.mediaType === "video" || (firstMedia && firstMedia.type === "video");
  const past = isPast(it);
  const temporal = temporalBadge(it);
  return (
    <div className={`relative h-56 overflow-hidden bg-[#0e0e0e] ${past ? "grayscale-[0.35]" : ""}`}>
      {src ? <div className="w-full h-full transition-transform duration-[1.2s] group-hover:scale-110"><FramedImage src={src} frame={it.imageFrame} alt={it.title} /></div> : <div className="w-full h-full" />}
      <span className="absolute top-4 left-4 text-[10px] tracking-[0.25em] uppercase text-electric bg-black/50 backdrop-blur px-3 py-1 rounded-full border border-electric/40">{it.category || "Actualité"}</span>
      <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
        {past
          ? <span className="text-[10px] tracking-[0.2em] uppercase text-purple-200 bg-black/60 backdrop-blur px-3 py-1 rounded-full border border-purple-400/40" data-testid="news-past-badge">{pastBadgeLabel(it)}</span>
          : isLocked(it) && <span className="text-[10px] tracking-[0.2em] uppercase text-amber-400 bg-black/60 backdrop-blur px-3 py-1 rounded-full border border-amber-400/40">Bientôt disponible</span>}
        {temporal && temporal.label !== "À venir" && <span className={`text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border backdrop-blur bg-black/50 ${temporal.tone}`} data-testid="news-temporal-badge">{temporal.label}</span>}
      </div>
      {isVideoCover && <span className="absolute inset-0 flex items-center justify-center"><span className="h-14 w-14 rounded-full bg-black/50 border border-white/40 flex items-center justify-center text-white group-hover:border-electric group-hover:text-electric transition-colors"><Play size={20} className="ml-0.5" /></span></span>}
      {it.mediaType === "slideshow" && !isVideoCover && <span className="absolute bottom-4 right-4 text-white/80"><Images size={18} /></span>}
    </div>
  );
};

export const News = () => {
  const { content } = useContent();
  const n = content.news;
  const items = publishedNews(n);
  if (!n || !items.length) return null;
  return (
    <section id="news" className="bg-surface py-24 md:py-40 border-t border-white/10" data-testid="news-section">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal className="mb-16 md:mb-24 max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-electric" />
            <span className="text-xs tracking-[0.35em] uppercase text-electric">{n.eyebrow}</span>
          </div>
          <h2 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95]">{n.heading}</h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.slice(0, 6).map((it, i) => (
            <Reveal key={it.id || i} delay={i * 0.1}>
              <Link to={`/actualites/${it.id}`} data-testid={`news-item-${i}`}
                className="group block rounded-lg overflow-hidden border border-white/10 bg-[#050505] hover:border-electric/40 transition-colors duration-500 h-full">
                <Thumb it={it} />
                <div className="p-6">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-secondary2 mb-3">{it.date}</div>
                  <h3 className="font-serif text-2xl text-white mb-3 flex items-start justify-between gap-3">
                    {it.title}
                    <ArrowUpRight size={18} className="text-electric shrink-0 mt-1 group-hover:rotate-45 transition-transform" />
                  </h3>
                  <p className="text-sm text-secondary2 font-light leading-relaxed">{it.excerpt}</p>
                  <span className="inline-block mt-4 text-[11px] tracking-[0.2em] uppercase text-electric">{n.ctaLabel || "Lire la suite"}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
